import type { Update } from '@ngrx/entity';
import type { ThemeSettingsState } from '@sites/site-theme/src/state';

export type HistoryChange =
    | AddHistoryChange
    | CopyHistoryChange
    | RemoveHistoryChange
    | MoveHistoryChange
    | UpdateHistoryChange
    | UpdateThemeHistoryChange
    | BatchHistoryChange;

export enum HistoryChangeAction {
    add = 'add',
    copy = 'copy',
    remove = 'remove',
    update = 'update',
    move = 'move',
    batch = 'batch',
}

export type AddHistoryChange = {
    action: HistoryChangeAction.add;
    payload: AddHistoryChangePayload;
};

type ChangePayload = {
    entityType: 'block' | 'section' | 'column' | 'grid' | 'legacyWidget' | 'legacyGridWidget' | 'tab';
    entity: any;
};

export type AddHistoryChangePayload = ChangePayload & {
    index: number;
    gridCellIndex?: number;
};

export type CopyHistoryChange = {
    action: HistoryChangeAction.copy;
    payload: CopyHistoryChangePayload;
};

export type CopyHistoryChangePayload = ChangePayload & {
    index: number;
    gridCellIndex?: number;
};

export type RemoveHistoryChange = {
    action: HistoryChangeAction.remove;
    payload: CopyHistoryChangePayload;
};

export type RemoveHistoryChangePayload = ChangePayload & {
    index: number;
    gridCellIndex?: number;
};

export type MoveHistoryChange = {
    action: HistoryChangeAction.move;
    payload: MoveHistoryChangePayload;
};

export type MoveHistoryChangePayload =
    | BlockHistoryMoveChangePayload
    | EntityHistoryMoveChangePayload
    | GridWidgetHistoryMoveChangePayload;

type BlockHistoryMoveChangePayload = {
    entityType: 'block';
    positionFrom: number;
    positionTo: number;
};

type EntityHistoryMoveChangePayload = {
    entityType: 'column' | 'legacyWidget';
    positionFrom: number;
    positionTo: number;
    prevParentId: string;
    nextParentId: string;
    entityId: string;
};

type GridWidgetHistoryMoveChangePayload = {
    entityType: 'legacyGridWidget';
    positionFrom: number;
    positionTo: number;
    prevParentId: string;
    nextParentId: string;
    entityId: string;
} & GridWidgetHistoryMoveChangePayloadType;

type GridWidgetHistoryMoveChangePayloadType =
    | {
          type: 'fromGridToColumn';
          fromCell: number;
      }
    | {
          type: 'fromColumnToGrid';
          toCell: number;
      }
    | {
          type: 'fromGridToGrid';
          fromCell: number;
          toCell: number;
      };

export type UpdateHistoryChange = {
    action: HistoryChangeAction.update;
    payload: UpdateHistoryChangePayload;
};

export type UpdateHistoryChangePayload =
    | {
          // Sections and single columns are recorded as v2 batch entries — see
          // `BatchUpdateEntry` — so they no longer appear here.
          entityType: 'block' | 'section' | 'grid' | 'legacyWidget' | 'tab' | 'store';
          before: Update<any>;
          after: Update<any>;
      }
    | {
          entityType: 'columns' | 'grids';
          before: Update<any>[];
          after: Update<any>[];
      };

export type UpdateThemeHistoryChange = {
    action: HistoryChangeAction.update;
    payload: UpdateThemeHistoryChangePayload;
};

/** The theme parts one change touched — a colour theme that re-colours the fonts carries both. */
export type UpdateThemeHistoryChangePayload = {
    entityType: 'theme';
    before: ThemeSettingsState;
    after: ThemeSettingsState;
};

export type BatchUpdateEntry = {
    action: HistoryChangeAction.update;
    entityType: string;
    before: Update<any>;
    after: Update<any>;
};

export type BatchAddEntry = {
    action: HistoryChangeAction.add;
    entityType: string;
    entity: any;
    index: number;
    gridCellIndex?: number;
};

export type BatchCopyEntry = {
    action: HistoryChangeAction.copy;
    entityType: string;
    entity: any;
    index: number;
    gridCellIndex?: number;
};

export type BatchRemoveEntry = {
    action: HistoryChangeAction.remove;
    entityType: string;
    entity: any;
    index: number;
    gridCellIndex?: number;
};

export type BatchMoveEntry = {
    action: HistoryChangeAction.move;
    payload: MoveHistoryChangePayload;
};

export type BatchEntry = BatchUpdateEntry | BatchAddEntry | BatchCopyEntry | BatchRemoveEntry | BatchMoveEntry;

export type BatchHistoryChange = {
    action: HistoryChangeAction.batch;
    version: 2;
    payload: {
        entries: BatchEntry[];
    };
    /** Set only on a change synthesized from one AI reply — see `MAX_AI_HISTORY_LENGTH` in the reducer. */
    origin?: 'ai';
};
